import type { JSX } from 'react';
import { memo } from 'react';

import { ChevronDown, CirclePlus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface SubProjectScoreConfig {
  minScore: number;
  considerAssignment: boolean;
}

interface PreviewUser {
  id: number;
  name: string;
}

interface SubProjectConfigCardProps {
  subProjectId: number;
  name: string;
  subConfig: SubProjectScoreConfig | null;
  recipientCount: number;
  hasCustomRecipients: boolean;
  previewUsers: PreviewUser[];
  hiddenUserCount: number;
  overlapCount: number;
  globalMinScore: number;
  globalConsiderAssignment: boolean;
  isExpanded: boolean;
  onToggleExpand: (subProjectId: number) => void;
  onUpdateScoreConfig: (subProjectId: number, next: { minScore?: number; considerAssignment?: boolean }) => void;
  onRemoveScoreConfig: (subProjectId: number) => void;
  onEditRecipients: (subProjectId: number) => void;
  onRemoveRecipients: (subProjectId: number) => void;
}

const yesNo = (value: boolean): string => (value ? 'sí' : 'no');

const SubProjectConfigCard = memo(
  ({
    subProjectId,
    name,
    subConfig,
    recipientCount,
    hasCustomRecipients,
    previewUsers,
    hiddenUserCount,
    overlapCount,
    globalMinScore,
    globalConsiderAssignment,
    isExpanded,
    onToggleExpand,
    onUpdateScoreConfig,
    onRemoveScoreConfig,
    onEditRecipients,
    onRemoveRecipients,
  }: SubProjectConfigCardProps): JSX.Element => {
    const summaryScore = subConfig ? subConfig.minScore : globalMinScore;
    const summaryAssignment = subConfig ? subConfig.considerAssignment : globalConsiderAssignment;
    const summaryParts = [`score ${summaryScore}`, `asignación ${yesNo(summaryAssignment)}`];
    if (recipientCount > 0) {
      summaryParts.push(`${recipientCount} ${recipientCount === 1 ? 'destinatario' : 'destinatarios'}`);
    }

    return (
      <Collapsible
        open={isExpanded}
        onOpenChange={() => onToggleExpand(subProjectId)}
        className="rounded-lg border border-border-subtle bg-bg-surface-raised"
      >
        <CollapsibleTrigger className="flex w-full items-center gap-3 px-4 py-3 text-left">
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-text-muted transition-transform',
              isExpanded ? 'rotate-0' : '-rotate-90'
            )}
            aria-hidden
          />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm font-semibold text-text-default font-display">{name}</span>
              {subConfig ? (
                <Badge variant="outline" className="border-brand-vivid/40 text-brand-vivid">
                  Reglas propias
                </Badge>
              ) : (
                <Badge variant="secondary">Hereda globales</Badge>
              )}
            </span>
            <span className="mt-0.5 block truncate text-xs text-text-muted font-body">{summaryParts.join(' · ')}</span>
          </span>
        </CollapsibleTrigger>

        <CollapsibleContent className="border-t border-border-subtle px-4 pb-4 pt-3">
          <div className="flex flex-wrap justify-end gap-2">
            {subConfig ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-error"
                onClick={() => onRemoveScoreConfig(subProjectId)}
              >
                <Trash2 className="size-4 shrink-0" aria-hidden />
                Quitar reglas
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => onUpdateScoreConfig(subProjectId, { minScore: 0, considerAssignment: false })}
              >
                <CirclePlus className="size-4 shrink-0" aria-hidden />
                Configurar reglas
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => onEditRecipients(subProjectId)}>
              Editar destinatarios ({recipientCount})
            </Button>
            {hasCustomRecipients ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-error"
                onClick={() => onRemoveRecipients(subProjectId)}
              >
                <Trash2 className="size-4 shrink-0" aria-hidden />
                Quitar destinatarios
              </Button>
            ) : null}
          </div>

          {subConfig ? (
            <div className="mt-3 rounded-lg border border-border-subtle bg-bg-surface p-3">
              <div className="flex flex-col gap-3">
                <div>
                  <span className="mb-1 block text-xs text-text-muted font-body">Score mínimo</span>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={subConfig.minScore}
                      aria-label={`Score mínimo para ${name}`}
                      className="w-full accent-brand-vivid"
                      onChange={(e) => onUpdateScoreConfig(subProjectId, { minScore: Number(e.target.value) })}
                    />
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={subConfig.minScore}
                      className="h-9 w-20 shrink-0"
                      aria-label={`Score mínimo numérico para ${name}`}
                      onChange={(e) => onUpdateScoreConfig(subProjectId, { minScore: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-sm font-body">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-input accent-brand-vivid"
                    checked={subConfig.considerAssignment}
                    onChange={(e) => onUpdateScoreConfig(subProjectId, { considerAssignment: e.target.checked })}
                  />
                  Considerar asignación de ejecutivo
                </label>
              </div>
            </div>
          ) : null}

          <div className="mt-3 flex flex-col gap-2 border-t border-border-subtle pt-3">
            <p className="m-0 text-xs font-semibold uppercase tracking-wider text-text-subtle font-body">
              Destinatarios extra
            </p>
            {previewUsers.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {previewUsers.map((user) => (
                  <Badge key={user.id} variant="outline" className="max-w-full truncate">
                    {user.name}
                  </Badge>
                ))}
                {hiddenUserCount > 0 ? <Badge variant="secondary">+{hiddenUserCount} más</Badge> : null}
              </div>
            ) : (
              <p className="m-0 text-sm text-text-subtle font-body">Sin destinatarios extra para este subproyecto.</p>
            )}
            {overlapCount > 0 ? (
              <p className="m-0 rounded-md border border-warning bg-warning-light px-2 py-1 text-xs text-neutral-900 font-body">
                {overlapCount === 1
                  ? 'Un usuario también está en la lista global.'
                  : 'Algunos usuarios también están en la lista global.'}
              </p>
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }
);

SubProjectConfigCard.displayName = 'SubProjectConfigCard';

export default SubProjectConfigCard;
